// src/api/axiosInstance.ts
import axios, { AxiosInstance, AxiosError, AxiosResponse } from "axios";
import { ApiResponse, ApiError } from "../types/apiResponse";
import { announcePassRequired, type PassRequired } from "@/lib/passGate";

const axiosInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("gt-access-token");
    if (token) {
      config.headers.Authorization = `${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

let isHandling401 = false;

// Response interceptor with enhanced error handling
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response && error.response.status === 401 && !isHandling401) {
      isHandling401 = true;
      localStorage.removeItem("gt-user");
      localStorage.removeItem("gt-access-token");
      window.location.replace("/");
    }

    const errorResponse: ApiError = {
      success: false,
      message: "An error occurred during the request",
      data: null,
      error: "Unknown error",
      errorCode: null,
    };

    if (error.response) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const serverResponse = error.response.data as Partial<ApiResponse<any>>;

      errorResponse.message =
        serverResponse.message || `Error: ${error.response.status}`;
      errorResponse.error = serverResponse.error || error.response.statusText;
      errorResponse.errorCode = serverResponse.errorCode ?? null;

      if (error.response.status === 402 && errorResponse.errorCode === "PASS_REQUIRED") {
        const q = (serverResponse.error ?? {}) as Partial<PassRequired>;
        announcePassRequired({ feature: q.feature ?? "mocks", used: q.used, limit: q.limit });
      }
    } else if (error.request) {
      errorResponse.message = "No response received from server";
      errorResponse.error = "Network error";
    } else {
      errorResponse.message = "Request configuration error";
      errorResponse.error = error.message;
    }

    if (import.meta.env.DEV) {
      console.error("API Error:", errorResponse);
    }

    return Promise.reject(errorResponse);
  }
);

export default axiosInstance;
